import { pointerIntersection } from "@dnd-kit/collision";
import {
  useDragDropManager,
  useDragDropMonitor,
  useDraggable,
  useDroppable,
} from "@dnd-kit/react";
import { clsObj } from "client-util/util";
import { useEvent } from "client-util/useEvent";
import { PuzzleLink, useOnClickRoutedLink } from "components/Link";
import { LargeSpinner } from "components/Loading";
import { Fabric } from "components/Material";
import MovableProvider, {
  useTransformContextSafe,
} from "components/Movables/MovableProvider";
import PieceView from "components/Movables/PieceView";
import {
  ROUND_VIEW_WIDTH_PX,
  getBaseRect,
  posRelTo,
  useMovableToRect,
  getElemCSSScale,
} from "components/Movables/pos";
import {
  DESERT_ISLAND_NUM_PIECES,
  DESERT_ISLAND_PIECE_SLUG_PREFIX,
} from "components/PuzzleContent/IslandFinder";
import {
  BASE_FONT_SIZE_PX,
  BASE_OUTER_WRAPPER_WIDTH_PX,
  FONT_SIZE_SCALING_PER_ROUND,
} from "components/PuzzlePage/PuzzleBodyDisplay";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import Tooltip from "components/Tooltip";
import { resolveRoute } from "components/urls";
import { AnimatePresence, HTMLMotionProps, motion } from "framer-motion";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import Twemoji from "react-twemoji";
import settings from "settings";
import { CursorEventType, CursorEvent } from "sockapi/cursors-messages";
import { RoundData } from "sockapi/puzzle-data";
import { ScopeType } from "sockapi/scope-types";
import { ROUND_IDS } from "sockapi/scope-specs/round-spec";
import { WSReqType } from "sockapi/sockapi-messages";
import { useRound } from "stores/NavStore";
import {
  useBox,
  useBoxIds,
  usePieceIds,
  useUpdatePieceInside,
} from "stores/RoundStore";
import {
  useIsPuzzleSolved,
  usePuzzleDisplayNames,
  useScopeStateResolvable,
  useTeamScopeState,
} from "stores/ScopeStateStore";
import {
  useTeamId,
  useSockClient,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import "./RoundView.css";

const getRoundBgImageStyle = (bgImage?: string) => ({
  backgroundImage:
    bgImage !== undefined ? `url("/static/images/bg/${bgImage}")` : undefined,
});

function AmbientBoxView({
  round,
  bgImage,
  ...rest
}: {
  round: RoundData;
  bgImage?: string;
} & HTMLMotionProps<"div">) {
  const isEndgame = round.roundId === ROUND_IDS.endgame;
  const isPosthunt = round.roundId === ROUND_IDS.posthunt;
  const { pieceIds } = useBox(null);
  const allPieceIds = usePieceIds();
  const boxRef = useRef<HTMLDivElement>(null);

  useDroppable({
    accept: ["boxPiece", "piece", "box"],
    id: "ambient-box",
    collisionPriority: -1,
    collisionDetector: pointerIntersection,
    element: boxRef,
  });

  useDragDropMonitor({
    onDragMove(event) {
      if (event.operation.target?.id === "ambient-box") {
        boxRef.current?.classList.add("drop-over");
      } else {
        boxRef.current?.classList.remove("drop-over");
      }
    },
    onDragEnd() {
      boxRef.current?.classList.remove("drop-over");
    },
  });

  return (
    <motion.div ref={boxRef} className="ambient-box" {...rest}>
      <div className="copy-only" style={{ userSelect: "all" }}>
        Copied {pieceIds.length} pieces out of {allPieceIds.length} total pieces
      </div>
      {isEndgame ? (
        <div
          className="endgame-bg"
          style={getRoundBgImageStyle("earth-round-bg.png")}
        />
      ) : (
        <Fabric
          seed={round.displayName}
          {...getRoundBgImageStyle(isPosthunt ? "kero-epic.png" : bgImage)}
        />
      )}
      <AnimatePresence>
        {pieceIds.map((pieceId) => (
          <PieceView key={pieceId} boxId={null} pieceId={pieceId} />
        ))}
      </AnimatePresence>
    </motion.div>
  );
}

export function BoxMenu({ boxId }: { boxId: string }) {
  const { box, pieceIds } = useBox(boxId);
  const allPieceIds = usePieceIds();
  const updateInside = useUpdatePieceInside();
  const displayNames = usePuzzleDisplayNames();

  if (!box || !displayNames) return null;

  const pieceList = pieceIds.map((pieceId) => {
    return (
      <div key={pieceId}>
        <Tooltip text="Remove from puzzle">
          <button
            onClick={() => {
              updateInside({ pieceId, boxId, add: false }).catch(console.error);
            }}
          >
            <Twemoji>📤</Twemoji>
          </button>
        </Tooltip>
        <PuzzleLink puzName={pieceId} type="puzzle" />
      </div>
    );
  });

  return (
    <div className="box-menu">
      {pieceIds.length >= 10 ? (
        <details>
          <summary>{pieceIds.length} pieces</summary>
          <div className="piece-menu-inside">{pieceList}</div>
        </details>
      ) : (
        <div className="piece-menu-inside">{pieceList}</div>
      )}
      <label className="piece-menu-add">
        Add piece:{" "}
        <select
          value={""}
          onChange={(e) => {
            if (e.target.value) {
              updateInside({ pieceId: e.target.value, boxId, add: true }).catch(
                console.error
              );
            }
          }}
        >
          <option value="">Select a piece...</option>
          {allPieceIds
            .filter((b) => !pieceIds.includes(b))
            .map((boxId) => (
              <option key={boxId} value={boxId}>
                {displayNames[boxId]}
              </option>
            ))}
        </select>
      </label>
    </div>
  );
}

function BoxView({
  boxId,
  className,
  ...rest
}: {
  boxId: string;
} & HTMLMotionProps<"div">) {
  const { box, pieceIds } = useBox(boxId);
  const toRect = useMovableToRect();
  const boxRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const isSolved = useIsPuzzleSolved(boxId);
  const manager = useDragDropManager();

  /*
  dump data usable for SolutionPieceGrid

  const puzzles = useAccessiblePuzzles();
  const movables = useMovables();

  console.log(
    Object.fromEntries(
      pieceIds.flatMap((pieceId) => {
        const movable = movables.find((m) => m.id === `${boxId}:${pieceId}`)!;
        if (!movable) return [];
        return [
          [
            puzzles[pieceId].puzName,
            {
              y: movable.row,
              x: movable.col,
              colspan: movable.cols,
              rowspan: movable.rows,
            },
          ],
        ];
      })
    )
  );
  */

  if (box === null) throw new Error(`box ${boxId} not found`);

  useDraggable({
    type: "box",
    id: boxId,
    element: dragRef,
    feedback: "none",
  });

  useDroppable({
    accept: ["boxPiece", "piece"],
    id: boxId,
    collisionPriority: box.order,
    collisionDetector: pointerIntersection,
    element: dragRef,
  });

  // super super hack: if a drag is starting from this box, give this box the
  // temporary highest priority, without needing to change the actual order
  const setPriority = (priority: number | undefined) => {
    if (manager === null) return;
    const droppable = [...manager.registry.droppables].find(
      (droppable) => droppable.id === boxId
    );
    if (droppable === undefined) return;
    droppable.collisionPriority = priority;
  };

  useDragDropMonitor({
    onDragStart(event) {
      const source = event.operation.source;
      if (boxRef.current?.contains(source?.element ?? null)) setPriority(303);
    },
    onDragMove(event) {
      if (event.operation.target?.id === boxId) {
        boxRef.current?.classList.add("drop-over");
      } else {
        boxRef.current?.classList.remove("drop-over");
      }
    },
    onDragEnd() {
      boxRef.current?.classList.remove("drop-over");
      setPriority(box?.order);
    },
  });

  const base = getBaseRect();
  if (base === null) return null;
  const rect = posRelTo(toRect(box, base), base);

  return (
    <motion.div
      ref={boxRef}
      className={clsObj({
        "box-view": true,
        solved: isSolved ?? false,
        ...(className !== undefined ? { [className]: true } : undefined),
      })}
      initial={rect}
      animate={rect}
      transition={{ duration: 0.2 }}
      {...rest}
    >
      <div className="box-content">
        <div className="box-body">
          <PuzzleContentDisplay
            puzName={box.id}
            isPreview
            fontSize={`${box.cols}em`}
          />
        </div>
        <div className="box-drag-target" ref={dragRef}>
          <div
            className={clsObj({
              "box-header": true,
              solved: isSolved ?? false,
            })}
          >
            <PuzzleLink
              puzName={box.id}
              type="puzzle"
              solved={isSolved ?? false}
            />
          </div>
          <div className="box-pieces">
            <AnimatePresence>
              {pieceIds.map((pieceId) => (
                <PieceView key={pieceId} boxId={box.id} pieceId={pieceId} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const useMatchScaleOnResize = () => {
  const transformCtx = useTransformContextSafe();
  return useEvent((baseElem: HTMLElement, containerElem: HTMLElement) => {
    const setScale = () => {
      const baseRect = baseElem.getBoundingClientRect();
      const containerRect = containerElem.getBoundingClientRect();
      const prevScale = getElemCSSScale(containerElem);
      const panZoomScale = transformCtx?.transformState.scale ?? 1;
      containerElem.style.scale = `${baseRect.width / (containerRect.width / panZoomScale / prevScale)}`;
    };
    const resizeObserver = new ResizeObserver(setScale);
    resizeObserver.observe(baseElem);
    setScale();
    return () => resizeObserver.disconnect();
  });
};

export function DesertIslandRoundView() {
  const round = useRound();
  const teamId = useTeamId();
  const teamScopeState = useTeamScopeState();
  const roundState = useScopeStateResolvable(
    ScopeType.ROUND,
    teamId !== null && round !== null
      ? { teamId, roundId: round.roundId }
      : null
  );
  const isReady =
    teamScopeState !== null && roundState !== null && round !== null;
  const piecesContainerRef = useRef<HTMLDivElement>(null);
  const onClickRoutedLink = useOnClickRoutedLink();
  const matchScaleOnResize = useMatchScaleOnResize();

  useEffect(() => {
    if (!isReady) return;
    if (piecesContainerRef.current === null) return;

    const initialPositions = Array.from(
      Array(DESERT_ISLAND_NUM_PIECES).keys(),
      () => ({ x: Math.random(), y: Math.random() })
    );

    const piecesContainerElem = piecesContainerRef.current;
    const containerRect = piecesContainerElem.getBoundingClientRect();
    // Pieces should all be the same size, so only call this once here.
    const pieceRect = piecesContainerElem.children[0].getBoundingClientRect();
    for (let i = 0; i < piecesContainerElem.children.length; i++) {
      const pieceElem = piecesContainerElem.children[i] as HTMLDivElement;
      const xPercent =
        (initialPositions[i].x * (containerRect.width - pieceRect.width)) /
        containerRect.width;
      const yPercent =
        (initialPositions[i].y * (containerRect.height - pieceRect.height)) /
        containerRect.height;
      pieceElem.style.top = `${yPercent * 30000}px`;
      pieceElem.style.left = `${xPercent * 3000}px`;
    }
    const roundBaseElem = document.getElementById("desert-round-base");
    if (roundBaseElem === null)
      throw new Error(
        "expect round base element to be rendered if pieces container is rendered"
      );
    return matchScaleOnResize(roundBaseElem, piecesContainerElem);
  }, [isReady, matchScaleOnResize]);

  const handlers = useMemo(() => {
    let pieceElem: HTMLDivElement | null = null;
    let parentElem: HTMLDivElement | null = null;
    let startX: number;
    let startY: number;
    let initialX: number;
    let initialY: number;
    let isDragging: boolean;
    let hasMoved: boolean = false;

    const onMouseDown = (e: MouseEvent) => {
      pieceElem = (e.target as HTMLDivElement).closest(
        ".desert-island-piece"
      ) as HTMLDivElement | null;
      parentElem = pieceElem?.parentElement as HTMLDivElement | null;
      if (!pieceElem || !parentElem) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = pieceElem.getBoundingClientRect();

      startX = e.clientX;
      startY = e.clientY;
      initialX = rect.x;
      initialY = rect.y;
      isDragging = false;
      hasMoved = false;

      pieceElem.classList.add("drag-focused");
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!pieceElem || !parentElem) return;

      e.preventDefault();
      e.stopPropagation();

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        isDragging = true;
        hasMoved = true;
      }

      if (isDragging) {
        const parentRect = parentElem.getBoundingClientRect();
        const scale = Number(piecesContainerRef.current!.style.scale);
        pieceElem.style.left = `${(initialX + dx) / scale}px`;
        pieceElem.style.top = `${(-parentRect.y + initialY + dy) / scale}px`;
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (pieceElem) {
        pieceElem.classList.remove("drag-focused");
        pieceElem = null;
        parentElem = null;
        e.preventDefault();
      }

      isDragging = false;
    };

    return {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      getHasMoved: () => hasMoved,
    };
  }, []);

  if (!isReady) {
    return <LargeSpinner inline={true} />;
  }

  return (
    <>
      <div className="ambient-box desert-island">
        <Fabric
          seed={round.displayName}
          className="desert-bg"
          {...getRoundBgImageStyle(roundState.bgImage)}
        />
        <div
          className="desert-island-pieces"
          ref={piecesContainerRef}
          onMouseDown={handlers.onMouseDown}
          onMouseMove={handlers.onMouseMove}
          onMouseUp={handlers.onMouseUp}
        >
          {Array.from(Array(DESERT_ISLAND_NUM_PIECES).keys(), (i) => {
            const pieceIndex = i + 1;
            const href = resolveRoute({
              route: "puzzle",
              params: {
                slug: `${DESERT_ISLAND_PIECE_SLUG_PREFIX}_${pieceIndex}`,
              },
            });
            return (
              <div key={pieceIndex} className="desert-island-piece piece-view">
                <a
                  href={href}
                  onClick={(e) => {
                    if (handlers.getHasMoved()) {
                      e.preventDefault();
                      e.stopPropagation();
                    } else {
                      onClickRoutedLink(e, href);
                    }
                  }}
                >
                  {pieceIndex}
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function CursorLayer() {
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const [cursors, setCursors] = useState<{
    [connId: string]: { rX: number; rY: number };
  }>({});
  const sockClient = useSockClient();

  useEffect(() => {
    if (sockClient === null || !shouldDisplayAdmin) return;
    return sockClient.onCursorEvent.addListener(({ events }) => {
      for (const { connId, event } of events) {
        if (event.type === CursorEventType.MOVE)
          setCursors((cursors) => ({ ...cursors, [connId]: event }));
        if (event.type === CursorEventType.REMOVE)
          setCursors((cursors) => {
            const { [connId]: _, ...rest } = cursors;
            return rest;
          });
      }
    });
  }, [sockClient, shouldDisplayAdmin]);

  if (sockClient === null || !shouldDisplayAdmin) return null;

  return (
    <div className="round-cursor-layer">
      {Object.entries(cursors).map(([connId, { rX, rY }]) => (
        <div
          key={connId}
          style={{ left: `${100 * rX}%`, top: `${100 * rY}%` }}
        />
      ))}
    </div>
  );
}

export default function RoundView() {
  const teamId = useTeamId();
  const round = useRound();
  const boxIds = useBoxIds();
  const roundState = useScopeStateResolvable(
    ScopeType.ROUND,
    teamId !== null && round !== null
      ? { teamId, roundId: round.roundId }
      : null
  );
  const context = useMemo(() => ({ type: "round" as const, round }), [round]);
  const roundPiecesRef = useRef<HTMLDivElement>(null);
  const matchScaleOnResize = useMatchScaleOnResize();
  const { hasAdminAccess } = settings;
  const cursorActive = useRef(false);
  const cursorPending = useRef<CursorEvent | null>(null);
  const sockClient = useSockClient();

  useEffect(() => {
    if (roundPiecesRef.current === null) return;

    const piecesContainerElem = roundPiecesRef.current;
    const roundBaseElem = document.getElementById("round-base");
    if (roundBaseElem === null)
      throw new Error(
        "expect round base element to be rendered if pieces container is rendered"
      );

    return matchScaleOnResize(roundBaseElem, piecesContainerElem);
  }, [matchScaleOnResize]);

  useEffect(() => {
    if (sockClient === null || round === null) return;
    return sockClient.addAuthEffect(() => {
      const reqId = Date.now();
      const cleanupEvent = sockClient.onSetCursorGroupAckEvent.addListener(
        (ackReqId) => {
          cursorActive.current = ackReqId === reqId;
        }
      );
      sockClient.sendReq({
        type: WSReqType.SET_CURSOR_GROUP,
        reqId,
        cursorGroupName: round.roundId,
      });
      return () => {
        cursorActive.current = false;
        cleanupEvent();
        sockClient.sendReq({
          type: WSReqType.SET_CURSOR_GROUP,
          reqId: 0,
          cursorGroupName: null,
        });
      };
    });
  }, [sockClient, round]);

  if (round === null) return <LargeSpinner inline />;
  const roundFontSizeScaling = FONT_SIZE_SCALING_PER_ROUND[round.roundId] ?? 1;
  const sendCursorEvent = (event: CursorEvent) => {
    if (!cursorActive.current) return;
    if (cursorPending.current === null)
      setTimeout(() => {
        if (
          sockClient === null ||
          cursorPending.current === null ||
          !cursorActive.current
        )
          return;
        sockClient.sendReq({
          type: WSReqType.CURSOR_EVENT,
          event: cursorPending.current,
        });
        cursorPending.current = null;
      }, 100);
    cursorPending.current = event;
  };

  return (
    <div
      id="round-pieces"
      ref={roundPiecesRef}
      style={{
        width: `${ROUND_VIEW_WIDTH_PX}px`,
        height: `${ROUND_VIEW_WIDTH_PX / 2}px`,
        fontSize: `${((BASE_FONT_SIZE_PX * ROUND_VIEW_WIDTH_PX) / (BASE_OUTER_WRAPPER_WIDTH_PX * round.gridCols)) * roundFontSizeScaling}px`,
      }}
      onMouseMove={(evt) => {
        if (hasAdminAccess) return;
        if (roundPiecesRef.current === null) return;
        const box = roundPiecesRef.current.getBoundingClientRect();
        sendCursorEvent({
          type: CursorEventType.MOVE,
          rX: (evt.clientX - box.left) / box.width,
          rY: (evt.clientY - box.top) / box.height,
        });
      }}
      onMouseLeave={(evt) => {
        if (hasAdminAccess) return;
        sendCursorEvent({ type: CursorEventType.REMOVE });
      }}
    >
      <CursorLayer />
      <MovableProvider context={context}>
        <AmbientBoxView round={round} bgImage={roundState?.bgImage} />
        {boxIds.map((boxId) => (
          <BoxView key={boxId} boxId={boxId} />
        ))}
      </MovableProvider>
    </div>
  );
}
